import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button} from 'antd';
import classNames from 'classnames';
import useCommon from '@utils/hooks/common';
import styles from './styles';
import {Loading3QuartersOutlined} from '@ant-design/icons';
import {motion} from 'framer-motion';
import {TournamentContext} from '@utils/context';

interface CanvasEmptySeatProps {
  onClick?: () => void;
  isSeated: boolean;
  position?: number;
  isAllIn?: boolean;
  loadingPosition?: number;
  isMyselfSeated?: boolean;
}

const CanvasEmptySeat: React.FC<CanvasEmptySeatProps> = React.memo(
  ({onClick, isSeated, position, isAllIn, loadingPosition, isMyselfSeated}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [emptySeatImg, setEmptySeatImg] = useState<HTMLImageElement | null>(null);
    const [occupiedSeatImg, setOccupiedSeatImg] = useState<HTMLImageElement | null>(null);
    const {classes, language} = useCommon({
      withStyles: styles,
      observers: ['language'],
    });

    const {isTournament} = useContext(TournamentContext);

    useEffect(() => {
      const getImgSrc = () => {
        if (language.currentLanguage === 'en-US') {
          return '/images/game-room/enter-seat-english.png';
        }
        if (language.currentLanguage === 'zh-HK') {
          return '/images-mobile/game-room/enter-seat.webp';
        }
        return '/images/game-room/enter-seat-no-words.svg';
      };

      const loadImages = async () => {
        const emptyImg = new Image();
        emptyImg.src = getImgSrc();
        await emptyImg.decode();
        setEmptySeatImg(emptyImg);

        const occupiedImg = new Image();
        occupiedImg.src = '/images-mobile/game-room/occupied-seat.webp';
        await occupiedImg.decode();
        setOccupiedSeatImg(occupiedImg);
      };

      loadImages();
    }, [language.currentLanguage]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (canvas && emptySeatImg && occupiedSeatImg) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          drawSeat(ctx, isSeated);
        }
      }
    }, [isSeated, emptySeatImg, occupiedSeatImg]);

    const drawSeat = (ctx: CanvasRenderingContext2D, isSeated: boolean) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

      const img = isSeated ? occupiedSeatImg : emptySeatImg;
      if (img) {
        ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    };

    return (
      <div
        className={classNames({
          [classes.seatPosition_0]: position === 0,
          [classes.seatPosition_1]: position === 1,
          [classes.seatPosition_2]: position === 2,
          [classes.seatPosition_3]: position === 3,
          [classes.seatPosition_4]: position === 4,
          [classes.seatPosition_5]: position === 5,
          [classes.seatPosition_0_allIn]: position === 0 && isAllIn,
          [classes.seatPosition_1_allIn]: position === 1 && isAllIn,
          [classes.seatPosition_2_allIn]: position === 2 && isAllIn,
          [classes.seatPosition_3_allIn]: position === 3 && isAllIn,
        })}
      >
        {!isTournament && (
          <>
            {loadingPosition === position && (
              // {true && (
              <motion.div
                className={classNames(classes.loading, {
                  seatPosition_0: position === 0,
                })}
                initial={{opacity: 0}}
                animate={{opacity: 0.8}}
                exit={{opacity: 0}}
                transition={{duration: 0.5}}
              >
                <Loading3QuartersOutlined
                  spin
                  style={{color: 'white', fontSize: position === 0 ? 20 : 15}}
                />
              </motion.div>
            )}

            <Button
              disabled={isSeated || isMyselfSeated}
              className={classNames(classes.invisibleButton)}
              onClick={onClick}
            >
              <canvas
                ref={canvasRef}
                width={120}
                height={120}
                className={classNames(classes.emptySeatImg, {
                  [classes.imageSizeForPosition0]: position === 0,
                  [classes.hidden]: isSeated,
                  [classes.darken]: isMyselfSeated,
                })}
              />
            </Button>
          </>
        )}
      </div>
    );
  }
);

CanvasEmptySeat.displayName = 'CanvasEmptySeat';

export default CanvasEmptySeat;
